import { CardTable, Toolbar, Modal } from 'shared'
import { notifyError, notifySuccess } from 'utils'
import { useNavigate, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { Formik, Form, Field } from 'formik'
import { Button, Row, Section, FormikErrorMessage } from 'shared/atoms'
import { Col } from 'rsuite'
import './Documents.scss'
import DefaultDocumentIcon from '../../../assets/images/default-document-image.jpg'
import {
  useUpdateDocumentMutation,
  useGetCompnayDocsQuery
} from '../companyApiSlice'
import { validationSchema } from '../utils'
import { ROUTES } from '../../../routers/Routes'
import { Documents, Status, Document } from '../types'

const DocumentVerification = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [imageModalOpen, setImageModalOpen] = useState(false)
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null
  )
  const [documentsWithStatus, setDocumentsWithStatus] = useState<Documents>([])
  const {
    refetch,
    data: companyUserList,
    isFetching
  } = useGetCompnayDocsQuery({ id }, { skip: !id })
  const [verifyDocument] = useUpdateDocumentMutation()

  useEffect(() => {
    if (companyUserList) {
      const documents = Array.isArray(companyUserList.documents)
        ? companyUserList.documents.map((doc: any) => doc)
        : []
      setDocumentsWithStatus([...documents])
    }
  }, [companyUserList])

  const handleReject = (document: Document) => {
    setSelectedDocument(document)
    setRejectModalOpen(true)
  }
  const handleVerifyDoc = async (documentId: number) => {
    if (!id) {
      notifyError('Cannot verify document.')
      return
    }
    try {
      const documentToUpdate = documentsWithStatus.find(
        (doc) => doc.id === documentId
      )
      if (documentToUpdate) {
        await verifyDocument({
          id: Number(id),
          documents: [
            {
              ...documentToUpdate,
              status: Status.Accepted,
              description: 'Verified'
            }
          ]
        }).unwrap()
        notifySuccess('Document verified successfully')
        refetch()
        setDocumentsWithStatus((prevDocs) =>
          prevDocs.map((doc) =>
            doc.id === documentId ? { ...doc, status: Status.Accepted } : doc
          )
        )
      } else {
        notifyError('Document not found')
      }
    } catch (error) {
      notifyError('Error in document verification')
    }
  }
  const handleRejectDoc = async (
    documentId: number,
    rejectionReason: string
  ) => {
    if (!id) {
      notifyError('Cannot reject document.')
      return
    }
    setDocumentsWithStatus((prevDocs) =>
      prevDocs.map((doc) =>
        doc.id === documentId ? { ...doc, status: Status.Rejected } : doc
      )
    )
    try {
      const documentToUpdate = documentsWithStatus.find(
        (doc) => doc.id === documentId
      )
      if (!documentToUpdate) {
        notifyError('Document not found.')
        return
      }

      await verifyDocument({
        id: Number(id),
        documents: [
          {
            ...documentToUpdate,
            status: Status.Rejected,
            description: rejectionReason
          }
        ]
      }).unwrap()
      notifySuccess('Document rejected successfully')
      refetch()
    } catch (error) {
      notifyError('Error in document rejection')
    }
  }
  const handleCardClick = (document: Document) => {
    if (!document.imageUrls) return

    const isPdf = document.imageUrls.toLowerCase().endsWith('.pdf')

    if (!isPdf) {
      setSelectedDocument(document)
      setImageModalOpen(true)
    }
  }
  const options = [
    {
      label: `Documents`,
      value: '',
      onClick: () => {}
    }
  ]
  return (
    <div className="user-list">
      <Toolbar
        options={options}
        total={documentsWithStatus.length}
        backButton="Back"
        backButtonClick={() => navigate(ROUTES.COMPANY_LIST)}
      />
      <div className="user-list__main-container">
        <CardTable
          documentCard
          data={documentsWithStatus}
          onReject={handleReject}
          handleVerifyDoc={handleVerifyDoc}
          handleCardClick={handleCardClick}
          loading={isFetching}
        />
      </div>
      <Modal
        secondary
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        title="Reject Document"
        size="sm"
        body={
          <div className="reject-document-container">
            {selectedDocument ? (
              <div className="document-section">
                <div className="document-info-container">
                  <p className="document-info">
                    <strong>Document Type: </strong>{' '}
                    {selectedDocument.documentType}
                  </p>
                  <p className="document-info">
                    <strong>Document No: </strong> {selectedDocument.documentNo}
                  </p>
                  {selectedDocument.imageUrls
                    ?.toLowerCase()
                    .endsWith('.pdf') ? (
                    <Button
                      appearance="primary"
                      className="view-button"
                      onClick={() =>
                        window.open(
                          selectedDocument.imageUrls,
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }
                    >
                      View Document
                    </Button>
                  ) : (
                    <img
                      src={selectedDocument.imageUrls ?? DefaultDocumentIcon}
                      alt="Rejected Document"
                      className="rejected-document-image"
                    />
                  )}
                </div>
              </div>
            ) : (
              <p>No document selected.</p>
            )}
            <Formik
              initialValues={{ rejectionReason: '' }}
              validationSchema={validationSchema}
              onSubmit={async (values, { resetForm }) => {
                if (!selectedDocument) {
                  notifyError('No document selected.')
                  return
                }
                handleRejectDoc(selectedDocument.id, values.rejectionReason)
                setRejectModalOpen(false)
                resetForm()
              }}
            >
              {({ handleSubmit, resetForm }) => (
                <Form onSubmit={handleSubmit} className="form-section">
                  <Section title="Reason">
                    <Row>
                      <Col xs={24}>
                        <Field
                          as="textarea"
                          name="rejectionReason"
                          placeholder="Enter reason..."
                          className="rs-input text-area-input"
                        />
                        <FormikErrorMessage name="rejectionReason" />
                      </Col>
                    </Row>
                    <div className="button-container">
                      <Button
                        type="button"
                        className="secondary reset-btn"
                        onClick={() => resetForm()}
                      >
                        Reset
                      </Button>
                      <Button type="submit" className="primary submit-btn">
                        Submit
                      </Button>
                    </div>
                  </Section>
                </Form>
              )}
            </Formik>
          </div>
        }
      />
      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        secondary
        title="Document Image"
        size="sm"
        body={
          selectedDocument ? (
            <div className="image-modal-container">
              <img
                src={selectedDocument.imageUrls ?? DefaultDocumentIcon}
                alt="Document"
                className="modal-image"
              />
            </div>
          ) : (
            <p>No image present.</p>
          )
        }
      />
    </div>
  )
}
export default DocumentVerification
